import { cn } from "./primitives/utils";

type LogoVariant = "full-color" | "subtle";

interface LogoProps {
  variant?: LogoVariant;
  size?: "sm" | "md" | "lg";
  showTagline?: boolean;
  className?: string;
}

export function Logo({
  variant = "full-color",
  size = "md",
  showTagline = false,
  className,
}: LogoProps) {
  const sizes = {
    sm: { height: 24 },
    md: { height: 32 },
    lg: { height: 40 },
  };

  const height = sizes[size].height;

  const renderLogo = () => {
    // Inline SVG used for both variants. The subtle variant applies a gray
    // filter via the wrapper element below.
    const subtleFilter =
      "brightness(0) saturate(100%) invert(47%) sepia(7%) saturate(427%) hue-rotate(180deg) brightness(93%) contrast(87%)";
    const isSubtle = variant === "subtle";

    const logoElement = (
      <svg
        viewBox="0 0 2048 528"
        height={height}
        style={{
          display: "block",
          width: "auto",
          marginLeft: "-20px",
          filter: isSubtle ? subtleFilter : undefined,
        }}
      >
        {/* Icon paths - keep magenta */}
        <path
          transform="translate(528,183)"
          d="m0 0h3l7 27 4 20 2 16 1 14v25l-2 24-5 26-7 25-10 25-10 20-14 22-12 16-13 15-18 18-11 9-15 11-14 9-18 10-7 4h-2l-2-11-2-31 1-32 3-23 6-25 9-26 12-28 16-33 17-34 10-18 7-14 10-14 9-10 13-13 14-11z"
          fill="#DA0070"
        />
        <path
          transform="translate(26,255)"
          d="m0 0h20l17 4 10 4 11 8 11 11 11 14 16 21 10 14 13 18 13 21 15 28 8 20 4 18 2 27-2 29-5 35-4 1-10-5-18-8-18-10-15-10-16-12-15-14-17-17-9-11-12-16-12-20-8-15-10-24-8-26-5-23-3-18v-36l11-5z"
          fill="#DA0070"
        />
        <path
          transform="translate(357,89)"
          d="m0 0h2l10 15 13 20 12 20 8 17 4 13 1 9v14l-2 13-5 13-8 17-11 18-9 13-10 14-14 19-16 21-11 15-10 14-14 22-9 17-8 20-3 9-4-2-9-14-8-19-4-13-4-18-3-16-3-29v-6l9-11 9-12 12-15 10-13 11-14 12-16 15-23 10-17 10-25 7-24 5-23 3-13z"
          fill="#DA0070"
        />
        <path
          transform="translate(289)"
          d="m0 0h8l15 2 5 3 5 17 10 24 7 13v10l-6 32-8 30-8 22-11 22-14 21-11 14-9 11-10 11-7 8-8 8-6 5-1 2h-2l-2-10-4-42-9-72v-12l3-13 8-13 26-36 12-19 11-23 5-14z"
          fill="#DA0070"
        />
        <path
          transform="translate(165,21)"
          d="m0 0 4 1 12 40 8 32 5 30 4 35 2 31 2 65v24l-1 28-2 19-3 8-8-5-12-11-7-8-11-13-11-14-11-15-7-12-3-10v-18l5-25 10-38 6-29 3-23v-36l-4-35-2-11z"
          fill="#DA0070"
        />
        <path
          transform="translate(115,49)"
          d="m0 0h2l4 23 1 11v33l-2 18-6 27-9 30-11 32-2 4-5-2-9-7-10-9-22-22-9-11-13-16 2-5 12-23 12-18 12-16 14-15 17-17 11-9z"
          fill="#DA0070"
        />
        <path
          transform="translate(451,67)"
          d="m0 0 8 6 18 18 11 14 9 12 12 19-1 5-13 11-16 11-17 9-8 1-4-6-4-18-3-19-1-23 2-18 6-21z"
          fill="#DA0070"
        />
        <path
          transform="translate(338,8)"
          d="m0 0 17 4 22 8 24 11 16 10v17l-3 19-5 17-8 16-7-6-10-12-12-17-14-22-11-20-10-24z"
          fill="#DA0070"
        />
        <path
          transform="translate(246)"
          d="m0 0h18l-1 7-6 16-10 18-11 15-9 11-7 8-5 5h-4l-11-29-9-24-4-12v-2l21-6 25-5z"
          fill="#DA0070"
        />
        <path
          transform="translate(13,186)"
          d="m0 0 7 6 9 11 13 14 7 8 4 4v2l-24-1-14 1-12 2 1-11 6-25z"
          fill="#DA0070"
        />

        {/* Text paths - dark color for readability */}
        <path
          transform="translate(909,187)"
          d="m0 0h23l17 4 16 8 9 7 7 6 9 12 7 14 4 13 2 16v9l-2 17-5 15-6 12-7 9-6 7-13 10-16 7-17 4h-23l-16-4-11-5-10-6-10-9-8-9-7-12-6-17-2-10v-26l4-17 8-17 9-12 8-8 14-9 11-5 11-3zm0 25-12 4-11 7-9 9-8 14-4 16v21l4 15 6 11 9 10 10 7 12 5 5 1h20l14-5 9-6 8-7 7-11 4-9 2-8 1-9v-10l-3-16-5-12-8-11-8-7-14-7-7-2z"
          fill="#1a1a1a"
        />
        <path
          transform="translate(1731,190)"
          d="m0 0h42l23 2 15 4 16 8 9 7 10 10 8 13 4 10 3 12 1 9v14l-2 13-4 13-6 12-9 11-7 7-11 7-10 5-13 4-11 2-12 1h-46zm28 24-2 2v113l6 1 24-1 15-4 12-7 9-8 6-10 4-11 2-12v-9l-2-13-4-11-6-10-10-9-14-7-13-3z"
          fill="#1a1a1a"
        />
        <path
          transform="translate(1556,190)"
          d="m0 0h46l21 2 12 4 10 6 5 4 7 10 4 10 1 4v19l-2 9-6 11-7 8-11 7-15 5h-6l6 8 18 20 9 11 10 11 9 11 2 4h-32l-9-10-9-11-12-14-11-13-9-11-4-4v61l-2 2h-25zm28 24-2 1v54l1 1h20l13-2 12-6 6-8 1-4v-16l-3-7-5-5-6-4-11-3z"
          fill="#1a1a1a"
        />
        <path
          transform="translate(1406,190)"
          d="m0 0h29l4 8 22 57 15 38 15 39 8 21-1 1h-27l-5-12-7-18-5-14-1-1h-66l-13 35-4 9-1 1h-26l-1-2 8-20 21-55 19-49 14-36zm13 31-23 61v3h49l-5-15-17-45-2-4z"
          fill="#1a1a1a"
        />
        <path
          transform="translate(687,190)"
          d="m0 0h42l22 2 15 5 10 6 9 8 6 10 4 11 1 6v14l-3 13-5 10-8 10-11 8-11 5-14 3-9 1h-23v51l-1 1h-24l-1-1v-162zm27 24-2 1v63h26l12-3 9-5 7-8 3-10v-12l-4-11-6-7-10-5-8-2z"
          fill="#1a1a1a"
        />
        <path
          transform="translate(1964,187)"
          d="m0 0h22l16 4 9 4 4 3 1 25-6-2-10-5-15-4h-18l-9 3-7 6-3 8 1 9 4 6 10 7 23 11 14 7 11 8 7 8 4 8 2 7v17l-3 10-6 11-7 7-10 6-11 4-10 2h-20l-17-4-16-8-3-2-1-27 4 1 10 7 14 6 9 2h18l10-4 7-5 4-8v-11l-3-6-8-7-22-11-19-9-9-7-7-7-5-10-2-7v-14l4-13 6-9 7-7 13-7z"
          fill="#1a1a1a"
        />
        <path
          transform="translate(1204,190)"
          d="m0 0h26v139h48l22 1 1 1v21l-4 2h-92l-1-1-1-59z"
          fill="#1a1a1a"
        />
        <path
          transform="translate(1058,190)"
          d="m0 0h26v139h47l23 1 1 13v9l-4 2h-92l-1-1z"
          fill="#1a1a1a"
        />
      </svg>
    );

    if (showTagline) {
      return (
        <div className={cn("flex flex-col gap-2", className)}>
          {logoElement}
          {/* Wider than the wordmark above it (~214px), so it's the piece
              that forces the logo block's width. Hidden until the row has
              room to spare — see DashboardLayout's nav breakpoint. */}
          <p className="hidden min-[1400px]:block text-[11px] text-muted-foreground tracking-wide uppercase ml-0.5">
            Customer Intelligence Platform
          </p>
        </div>
      );
    }

    return <div className={className}>{logoElement}</div>;
  };

  return renderLogo();
}
